import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = ["Under $500/month", "$500-$2,000", "$2,000-$5,000", "$5,000-$15,000", "$15,000+", "Not sure"];
const TEAMS = ["Just me", "2-5", "6-20", "21-100", "100+"];

export default function AuditPage() {
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [f, setF] = useState({
    business_name: "", contact_name: "", email: "", phone: "", industry: "",
    website_url: "", instagram: "", facebook: "", linkedin: "", twitter: "", youtube: "",
    current_marketing: "", monthly_budget: "", goals: "", challenges: "",
    team_size: "", target_audience: "", competitors: "", additional_info: ""
  });
  const u = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name || !f.contact_name || !f.email || !f.phone) { toast.error("Fill in all required fields."); return; }
    setSending(true);
    try { await axios.post(`${API}/audit`, f); setDone(true); toast.success("Submitted!"); window.scrollTo(0, 0); }
    catch { toast.error("Something went wrong. Try again."); }
    setSending(false);
  };

  if (done) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center max-w-md">
        <CheckCircle className="w-10 h-10 text-white mx-auto" />
        <h1 className="text-2xl md:text-3xl tracking-tight mt-8">Audit request received.</h1>
        <p className="text-zinc-400 text-xs mt-4 tracking-wider">Our team will review your business and get back within 48 hours.</p>
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
          <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I%20just%20submitted%20an%20audit%20request.%20Looking%20forward%20to%20your%20review." target="_blank" rel="noopener noreferrer" data-testid="audit-success-whatsapp"
            className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center justify-center gap-3">
            Whatsapp us <ArrowRight className="w-4 h-4" />
          </a>
          <Link to="/" data-testid="audit-success-home"
            className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors text-center">
            Home
          </Link>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white pt-28 pb-24">
      <div className="px-6 md:px-16 lg:px-28 max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] text-zinc-500 hover:text-white transition-colors mb-10 tracking-[0.2em]" data-testid="audit-back-link">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-6">Free audit</p>
          <h1 className="text-3xl md:text-4xl tracking-tight" data-testid="audit-page-title">
            Tell us about your business.
          </h1>
          <p className="text-zinc-500 text-xs mt-4 tracking-wider max-w-lg">
            The more we know, the better our audit. Completely free. No strings attached.
          </p>
        </motion.div>

        <form onSubmit={submit} className="mt-16 space-y-12">
          {/* Basic Info */}
          <div>
            <p className="text-xs tracking-[0.2em] text-zinc-500 border-b border-white/10 pb-4 mb-8">Basic information</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Business name *" id="audit-business-name" value={f.business_name} onChange={v => u("business_name", v)} ph="Your business" />
              <Field label="Your name *" id="audit-contact-name" value={f.contact_name} onChange={v => u("contact_name", v)} ph="Full name" />
              <Field label="Email *" id="audit-email" value={f.email} onChange={v => u("email", v)} ph="you@email.com" type="email" />
              <Field label="Phone *" id="audit-phone" value={f.phone} onChange={v => u("phone", v)} ph="+91 XXXXX XXXXX" />
              <Field label="Industry" id="audit-industry" value={f.industry} onChange={v => u("industry", v)} ph="E.g. Ecommerce, Real Estate" />
              <Field label="Website" id="audit-website" value={f.website_url} onChange={v => u("website_url", v)} ph="https://..." />
            </div>
          </div>

          {/* Social */}
          <div>
            <p className="text-xs tracking-[0.2em] text-zinc-500 border-b border-white/10 pb-4 mb-8">Social media</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { k: "instagram", l: "Instagram", ph: "@handle" },
                { k: "facebook", l: "Facebook", ph: "Page url" },
                { k: "linkedin", l: "Linkedin", ph: "Company url" },
                { k: "twitter", l: "Twitter / X", ph: "@handle" },
                { k: "youtube", l: "Youtube", ph: "Channel url" }
              ].map(s => <Field key={s.k} label={s.l} id={`audit-${s.k}`} value={f[s.k]} onChange={v => u(s.k, v)} ph={s.ph} />)}
            </div>
          </div>

          {/* Marketing */}
          <div>
            <p className="text-xs tracking-[0.2em] text-zinc-500 border-b border-white/10 pb-4 mb-8">Marketing details</p>
            <div className="space-y-6">
              <FieldArea label="What marketing are you doing now?" id="audit-current-marketing" value={f.current_marketing} onChange={v => u("current_marketing", v)} ph="Social media, ads, nothing..." />
              <div>
                <p className="text-[10px] tracking-[0.15em] text-zinc-500 mb-3">Monthly budget</p>
                <div className="flex flex-wrap gap-2">
                  {BUDGETS.map(b => (
                    <Chip key={b} active={f.monthly_budget === b} onClick={() => u("monthly_budget", b)} label={b} testId={`audit-budget-${b}`} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.15em] text-zinc-500 mb-3">Team size</p>
                <div className="flex flex-wrap gap-2">
                  {TEAMS.map(t => (
                    <Chip key={t} active={f.team_size === t} onClick={() => u("team_size", t)} label={t} testId={`audit-team-${t}`} />
                  ))}
                </div>
              </div>
              <FieldArea label="Your goals" id="audit-goals" value={f.goals} onChange={v => u("goals", v)} ph="More sales, better branding, hire a team..." />
              <FieldArea label="Biggest challenges" id="audit-challenges" value={f.challenges} onChange={v => u("challenges", v)} ph="Budget, time, knowledge..." />
              <FieldArea label="Target audience" id="audit-target" value={f.target_audience} onChange={v => u("target_audience", v)} ph="Who are your customers?" />
              <FieldArea label="Competitors" id="audit-competitors" value={f.competitors} onChange={v => u("competitors", v)} ph="Who do you compete with?" />
              <FieldArea label="Anything else" id="audit-additional" value={f.additional_info} onChange={v => u("additional_info", v)} ph="Anything we should know" />
            </div>
          </div>

          <Button type="submit" disabled={sending} data-testid="audit-submit-btn"
            className="bg-white text-black hover:bg-zinc-200 rounded-none px-10 py-4 text-xs tracking-[0.2em] h-auto disabled:opacity-50 w-full md:w-auto">
            {sending ? "Sending..." : "Submit audit request"} {!sending && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, id, value, onChange, ph, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">{label}</label>
      <Input data-testid={id} type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={ph} className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
    </div>
  );
}

function FieldArea({ label, id, value, onChange, ph }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">{label}</label>
      <Textarea data-testid={id} value={value} onChange={e => onChange(e.target.value)}
        placeholder={ph} rows={3} className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 rounded-none text-xs tracking-wider resize-none" />
    </div>
  );
}

function Chip({ active, onClick, label, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`text-[10px] tracking-wider px-4 py-2 border transition-colors ${active ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
      {label}
    </button>
  );
}
